/* The person window's checks, kept out of the screen code so they can be tested.
   - Cost rows: a completely empty row (no date, no rate) is ignored, so a person can be saved with no cost at all
     (unknown, not $0). A row with a date but no rate is an error that names the row. Blank rate is never turned into 0.
   - E-mail: one e-mail belongs to one person (the database has a case-insensitive unique index, members_email_ci);
     the window says who already has it before trying to save.
   - Database errors: known constraint names become plain sentences. */
import type {CostRate, Member} from './model.ts';

export interface CostRow { from: string; rate: string }   // as typed: from '' = since the beginning, rate '' = not filled in
export type CostCheck = {ok: true; rows: CostRate[]} | {ok: false; index: number; field: 'from' | 'rate'; msg: string};

/** 2026-03-01 -> Mar 1, 2026 (for messages) */
export function niceDate(d: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
  if (!m) return d;
  return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][+m[2] - 1] + ' ' + (+m[3]) + ', ' + m[1];
}

export function checkCostRows(rows: CostRow[]): CostCheck {
  const out: (CostRate & {i: number})[] = [];
  for (let i = 0; i < rows.length; i++) {
    const from = (rows[i].from || '').trim(), raw = (rows[i].rate || '').trim();
    if (!from && !raw) continue;                                                       // empty row: ignored
    const where = from ? `the row starting ${niceDate(from)}` : 'the row with no start date';
    if (!raw) return {ok: false, index: i, field: 'rate', msg: `Enter the cost for ${where}, or remove the row.`};
    const rate = Number(raw);
    if (!isFinite(rate) || rate < 0) return {ok: false, index: i, field: 'rate', msg: `The cost for ${where} must be a number, 0 or more.`};
    if (from && !/^\d{4}-\d{2}-\d{2}$/.test(from)) return {ok: false, index: i, field: 'from', msg: 'Pick a start date for this row, or leave it blank for "since the beginning".'};
    const twin = out.find(r => r.from === from);
    if (twin) return {ok: false, index: i, field: 'from', msg: from ? `Two cost rows start on ${niceDate(from)}. Change one date or remove a row.` : 'Two cost rows have no start date. Give one of them a date or remove it.'};
    out.push({from, rate, i});
  }
  return {ok: true, rows: out.sort((a, b) => a.from.localeCompare(b.from)).map(({from, rate}) => ({from, rate}))};
}

const norm = (e: string | null | undefined) => (e || '').trim().toLowerCase();
/** someone else who already has this e-mail (case and spaces ignored), or undefined */
export function emailOwner(members: Member[], email: string, selfId?: string): Member | undefined {
  const e = norm(email);
  return e ? members.find(m => m.id !== selfId && norm(m.email) === e) : undefined;
}

/** a database error message in plain language ('' = not one we know) */
export function friendlyDbError(msg: string): string {
  const m = String(msg || '');
  if (m.includes('members_email_ci')) return 'This e-mail already belongs to someone else in Team. Open them in Team to edit instead.';
  if (m.includes('members_one_owner')) return 'There can only be one owner.';
  if (m.includes('clients_name_ci')) return 'Another client already has this name. Use Merge clients to combine them.';
  if (m.includes('projects_client_name_ci')) return 'This client already has a project with that name.';
  if (m.includes('tasks_project_name_ci')) return 'This project already has a task with that name.';
  if (m.includes('member_cost_rates_pkey')) return 'Two cost rows start on the same date.';
  if (/violates check constraint .*(rate|bill_rate)/.test(m)) return 'Rates must be 0 or more.';
  if (/update or delete on table .* violates foreign key constraint/.test(m)) return 'Time entries still use this, so it cannot be removed. Archive it instead.';
  return '';
}
